import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

type Props = {
  images: string[];
  initialIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ImageLightbox({ images, initialIndex, open, onOpenChange }: Props) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setCurrentIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') setCurrentIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setCurrentIndex((i) => Math.min(images.length - 1, i + 1));
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, images.length]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-ink/90" />
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-cream hover:bg-white/20 rounded-full"
              aria-label="Close"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </Button>
          </DialogClose>

          {images.length > 1 && (
            <span className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-ink/50 px-3 py-1 text-sm text-cream select-none">
              {currentIndex + 1} / {images.length}
            </span>
          )}

          <img
            src={images[currentIndex]}
            alt=""
            className="max-h-[85vh] max-w-full rounded-lg object-contain select-none"
            draggable={false}
          />

          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                disabled={!hasPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-cream hover:bg-white/20 rounded-full disabled:opacity-30"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentIndex((i) => Math.min(images.length - 1, i + 1))}
                disabled={!hasNext}
                className="absolute right-16 top-1/2 -translate-y-1/2 text-cream hover:bg-white/20 rounded-full disabled:opacity-30"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" aria-hidden="true" />
              </Button>
            </>
          )}
        </div>
      </DialogPortal>
    </Dialog>
  );
}
